import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import { Box, Link, Button, Drawer, Typography, Avatar, Stack } from '@mui/material';
import useResponsive from '../../../hooks/useResponsive';
import Logo from '../../../components/logo';
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';
import generateNavConfig from './config';
import { useAuth } from 'hooks/useAuthContext';

const NAV_WIDTH = 280;

const StyledAccount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.grey[500], 0.12),
}));

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

/**
 * @requires
 * - `useAuth` from `hooks/useAuthContext`
 * - `useLocation` from `react-router-dom`
 * - `useResponsive` from `hooks/useResponsive`
 * - `generateNavConfig` from `./config`
 * - `Scrollbar` from `components/scrollbar`
 * - `Logo` from `components/logo`
 * - `NavSection` from `components/nav-section`
 *
 * @param {object} props
 * @param {boolean} props.openNav
 * @param {function} props.onCloseNav
 *
 * @returns {ReactElement} A navigation component that renders a drawer or a permanent nav
 * based on the screen size.
 */
export default function Nav({ openNav, onCloseNav }) {
  const { loading, user } = useAuth();
   
  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg');
   
  const navConfig = generateNavConfig(user?.user_type);
  
  
  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Box sx={{ px: 2.5, py: 2, paddingTop: 5, display: 'inline-flex' }}>
        {/* <Logo /> */}
      </Box>

      <Box sx={{ mb: 5, mx: 2.5 }}>
        <Link underline="none">
          <StyledAccount>
            <Avatar src={user?.photo} alt="photoURL" />

            <Box sx={{ ml: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <Typography variant="subtitle2" sx={{ color: 'text.primary' }} noWrap>
                {user?.name} 
              </Typography>

              <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                {user?.user_type}
              </Typography>
            </Box>
          </StyledAccount>
        </Link>
      </Box>

      <NavSection data={navConfig} />

      <Box sx={{ flexGrow: 1 }} />

      
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV_WIDTH },
      }}
    >
      {isDesktop ? (<>
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              bgcolor: 'background.default',
              borderRightStyle: 'dashed',
              zIndex: '998'
            },
          }}
        >
          {renderContent}
        </Drawer>
      </>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: { width: NAV_WIDTH },
              zIndex: '998'
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
